/**
 * Who wrote a message, or started a thread, as the avatars draw them. A
 * person's message carries its author (slice 6a), removed or not; one that
 * doesn't, and isn't the signed-in person's, reads as "A teammate".
 */
import type { AgentTint, PersonTint } from "@crew/ui/components/crew-avatar";
import type { Me } from "../../app/me";
import { agentTint, personTint } from "../../app/tints";
import type { ThreadItem, ThreadMessage } from "./api";

export type Face =
  | { kind: "agent"; name: string; glyph: string; tint: AgentTint }
  | { kind: "person"; name: string; initials: string; tint: PersonTint };

const CHIEF: Face = { kind: "agent", name: "Chief of Staff", glyph: "C", tint: "ink" };
const TEAMMATE: Face = { kind: "person", name: "A teammate", initials: "?", tint: "sand" };

function agentFace(me: Me, agentId: string): Face {
  const agent = me.agents.find((a) => a.id === agentId);
  if (!agent) return CHIEF;
  return { kind: "agent", name: agent.name, glyph: agent.glyph, tint: agentTint(agent.tint) };
}

export function authorOf(
  message: Pick<ThreadMessage, "author_agent_id" | "author_user_id" | "author">,
  me: Me,
): Face {
  if (message.author_agent_id) return agentFace(me, message.author_agent_id);
  const mine = message.author_user_id === me.user.id;
  const by = message.author?.id === message.author_user_id ? message.author : null;
  const person = by ?? (mine ? me.user : null);
  if (!person) return TEAMMATE;
  return {
    kind: "person",
    name: person.name.split(/\s+/)[0] || person.name,
    initials: person.initials,
    tint: personTint(person.tint),
  };
}

/** The person who started a thread; a routine's or the system's has none. */
export function starterOf(thread: Pick<ThreadItem, "started_by">): Face & { kind: "person" } {
  const by = thread.started_by;
  if (!by) return { kind: "person", name: "Crew", initials: "·", tint: "sand" };
  return { kind: "person", name: by.name, initials: by.initials, tint: personTint(by.tint) };
}

/** The Chief of Staff (`slug: main`), who answers every thread in slice 1. */
export function chiefOf(me: Me): Face {
  const main = me.agents.find((a) => a.slug === "main");
  return main ? agentFace(me, main.id) : CHIEF;
}
